/**
 * Eigene Nutzungsanalyse (Nutzungsbedingungen § 8) — kein Drittanbieter.
 *
 * Erfasst Seitenaufrufe, Klicks, Fehler/Abstürze, Ladezeiten und KI-Aufrufe,
 * bündelt sie im Speicher und schickt sie alle 10 s per RPC `nutzung_erfassen`.
 * Der Betrieb kommt serverseitig aus dem Login. Kein Cookie, kein localStorage:
 * die Sitzungs-ID lebt nur im Tab.
 *
 * Datensparsamkeit: Klick-Beschriftungen nur, wenn sie nach UI-Text aussehen
 * (kurz, ohne Ziffern/@, nicht in Listen/Tabellen — dort stehen Kundennamen).
 * IDs in Pfaden werden zu :id. Keine Formularinhalte.
 */

import { setFetchBeobachter } from './supabaseClient';

const URL_BASIS = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
const APP_VERSION = process.env.REACT_APP_BUILD || 'dev';
const KI_FUNKTIONEN = /^(ai|ai-.+|aufmass|onboarding-extract|beleg-extract|belegwissen|lv-matching)$/;
const INTERVALL_MS = 10_000;
const AKTIV_PING_MS = 60_000;

let sitzung = null;
let tokenHolen = null;
let warteschlange = [];
let timer = null;
let pingTimer = null;
let letzteSeite = null;
let letzteInteraktion = 0;
// Ladezeiten kommen oft vor dem Login — kurz zwischenspeichern
let vorab = [];
const letzteFehler = new Map();

const neueId = () => (crypto.randomUUID ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => ((Math.random() * 16) | 0).toString(16)));

export function normalisierePfad(pfad) {
    return pfad
        .split('/')
        .map((teil) => (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(teil) || /^\d+$/.test(teil) || /^[A-Za-z0-9_-]{20,}$/.test(teil) ? ':id' : teil))
        .join('/') || '/';
}

function erfasse(art, name, daten) {
    const event = { sitzung, plattform: 'web', art, name: String(name).slice(0, 160), daten, zeit: String(Date.now()) };
    if (!sitzung) {
        if (art === 'leistung' && vorab.length < 20) vorab.push(event);
        return;
    }
    warteschlange.push(event);
    if (warteschlange.length >= 50) senden();
}

async function senden({ beimSchliessen = false } = {}) {
    if (!warteschlange.length || !tokenHolen) return;
    const paket = warteschlange.splice(0, 200);
    try {
        const token = await tokenHolen();
        if (!token) return;
        const res = await fetch(`${URL_BASIS}/rest/v1/rpc/nutzung_erfassen`, {
            method: 'POST',
            keepalive: beimSchliessen,
            headers: { apikey: ANON, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ p_events: paket }),
        });
        if (!res.ok && res.status >= 500 && !beimSchliessen) warteschlange.unshift(...paket.slice(0, 100));
    } catch {
        // Analytics darf nie die App stören — beim nächsten Mal weiter
        if (!beimSchliessen) warteschlange.unshift(...paket.slice(0, 100));
    }
}

function geraet() {
    const ua = navigator.userAgent;
    const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android'
        : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'unbekannt';
    const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox'
        : /Safari\//.test(ua) ? 'Safari' : 'unbekannt';
    return {
        os, browser,
        mobil: /Mobi|iPhone|Android/.test(ua),
        bildschirm: `${window.screen.width}×${window.screen.height}`,
        fenster: `${window.innerWidth}×${window.innerHeight}`,
        sprache: navigator.language,
        app_version: APP_VERSION,
    };
}

function seiteErfassen() {
    const pfad = normalisierePfad(window.location.pathname);
    if (pfad === letzteSeite) return;
    letzteSeite = pfad;
    erfasse('seite', pfad);
}

export function klickName(el) {
    const track = el.closest('[data-track]')?.getAttribute('data-track');
    if (track) return track;
    const tag = el.tagName.toLowerCase();
    if (el.closest('li, tr, table, [role=row], [role=listitem], [role=option], [role=gridcell]')) return `(${tag} in Liste)`;
    const kandidat = (el.getAttribute('aria-label') || el.innerText || el.getAttribute('title') || '').trim().replace(/\s+/g, ' ');
    if (!kandidat || kandidat.length > 30 || /[\d@€]/.test(kandidat)) return `(${tag})`;
    return kandidat;
}

function beiKlick(e) {
    letzteInteraktion = Date.now();
    const el = e.target?.closest?.('button, a, [role=button], [role=tab], [role=menuitem], [role=switch], [role=checkbox]');
    if (!el) return;
    erfasse('klick', klickName(el), { seite: normalisierePfad(window.location.pathname) });
}

export function fehlerErfassen(quelle, fehler, extra) {
    const meldung = String(fehler?.message || fehler || 'Unbekannter Fehler').slice(0, 300);
    const schluessel = `${quelle}|${meldung}`;
    const jetzt = Date.now();
    if (jetzt - (letzteFehler.get(schluessel) || 0) < 10_000) return;
    letzteFehler.set(schluessel, jetzt);
    erfasse('fehler', fehler?.name || quelle, {
        quelle,
        meldung,
        stack: String(fehler?.stack || '').slice(0, 1500) || undefined,
        seite: normalisierePfad(window.location.pathname),
        geraet: geraet(),
        ...extra,
    });
}

function beiFehler(e) {
    if (!e.error && /ResizeObserver loop/.test(e.message || '')) return;
    fehlerErfassen('window', e.error || e.message, e.filename ? { datei: `${e.filename.split('/').pop()}:${e.lineno}:${e.colno}` } : undefined);
}

function beiAbgelehnt(e) {
    fehlerErfassen('promise', e.reason);
}

/**
 * Fehlertext aus der Antwort: Edge Functions schicken { error }, PostgREST
 * { message, details, hint, code }. Sonst der Anfang des Textes.
 */
export function antwortMeldung(antwort) {
    if (!antwort) return '';
    try {
        const j = JSON.parse(antwort);
        const teile = [j.error?.message || j.error || j.message || j.msg || j.error_description, j.details, j.hint, j.code && `(${j.code})`];
        const text = teile.filter((t) => typeof t === 'string' && t.trim()).join(' · ');
        if (text) return text.slice(0, 400);
    } catch { /* kein JSON */ }
    return String(antwort).replace(/\s+/g, ' ').trim().slice(0, 200);
}

/** Jede Supabase-Anfrage läuft hier vorbei: Edge Functions immer, REST nur bei Fehler oder > 3 s. */
function beobachte(url, methode, status, dauerMs, antwort) {
    if (!sitzung) return;
    const fehler = status === 0 || status >= 400;
    const grund = antwortMeldung(antwort);
    const fn = url.match(/\/functions\/v1\/([^/?#]+)/)?.[1];
    if (fn) {
        const daten = { dauer_ms: Math.round(dauerMs), status, ok: !fehler };
        erfasse(KI_FUNKTIONEN.test(fn) ? 'ki' : 'leistung', KI_FUNKTIONEN.test(fn) ? fn : `fn:${fn}`, daten);
        if (fehler) {
            erfasse('fehler', `fn:${fn}`, {
                quelle: 'function',
                meldung: `${fn} → ${status || 'Netzwerk'}${grund ? `: ${grund}` : ''}`,
                seite: normalisierePfad(window.location.pathname),
                geraet: geraet(),
            });
        }
        return;
    }
    const rest = url.match(/\/rest\/v1\/(rpc\/)?([^/?#]+)/);
    if (!rest || rest[2] === 'nutzung_erfassen') return;
    const ziel = `${rest[1] ? 'rpc:' : ''}${rest[2]}`;
    if (fehler) {
        erfasse('fehler', 'api', {
            quelle: 'api',
            meldung: `${methode || 'GET'} ${ziel} → ${status || 'Netzwerk'}${grund ? `: ${grund}` : ''}`,
            seite: normalisierePfad(window.location.pathname),
            geraet: geraet(),
        });
    } else if (dauerMs > 3000) {
        erfasse('leistung', `langsam:${ziel}`, { wert: Math.round(dauerMs) });
    }
}

function beiSichtbarkeit() {
    if (document.visibilityState === 'hidden') senden({ beimSchliessen: true });
}

/** Ab Login mit Betrieb. `holeToken` liefert das aktuelle Access-Token. */
export function nutzungStarten(holeToken) {
    tokenHolen = holeToken;
    if (sitzung) return;
    sitzung = neueId();
    letzteSeite = null;
    letzteInteraktion = Date.now();

    const nav = performance.getEntriesByType?.('navigation')?.[0];
    erfasse('sitzung', 'start', {
        ...geraet(),
        ladezeit_ms: nav ? Math.round(nav.loadEventEnd || nav.domContentLoadedEventEnd) : undefined,
    });
    seiteErfassen();
    warteschlange.push(...vorab.map((e) => ({ ...e, sitzung })));
    vorab = [];

    for (const methode of ['pushState', 'replaceState']) {
        const original = window.history[methode];
        if (original.__nutzung) continue;
        const gepatcht = function (...args) {
            const r = original.apply(this, args);
            setTimeout(seiteErfassen, 0);
            return r;
        };
        gepatcht.__nutzung = true;
        window.history[methode] = gepatcht;
    }
    window.addEventListener('popstate', seiteErfassen);
    document.addEventListener('click', beiKlick, true);
    document.addEventListener('keydown', () => { letzteInteraktion = Date.now(); }, { passive: true });
    window.addEventListener('error', beiFehler);
    window.addEventListener('unhandledrejection', beiAbgelehnt);
    document.addEventListener('visibilitychange', beiSichtbarkeit);
    setFetchBeobachter(beobachte);

    timer = setInterval(() => senden(), INTERVALL_MS);
    // Sitzungsdauer: nur zählen, solange wirklich jemand arbeitet
    pingTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && Date.now() - letzteInteraktion < AKTIV_PING_MS) erfasse('aktiv', 'ping');
    }, AKTIV_PING_MS);
}

/** Vor dem Abmelden: Rest senden, solange das Token noch gilt. */
export async function nutzungBeenden() {
    if (!sitzung) return;
    await senden();
    clearInterval(timer);
    clearInterval(pingTimer);
    window.removeEventListener('popstate', seiteErfassen);
    document.removeEventListener('click', beiKlick, true);
    window.removeEventListener('error', beiFehler);
    window.removeEventListener('unhandledrejection', beiAbgelehnt);
    document.removeEventListener('visibilitychange', beiSichtbarkeit);
    setFetchBeobachter(null);
    sitzung = null;
    tokenHolen = null;
}

/** Web Vitals (index.js → reportWebVitals) */
export function leistungErfassen(metrik) {
    erfasse('leistung', metrik.name, { wert: Math.round(metrik.value * (metrik.name === 'CLS' ? 1000 : 1)) / (metrik.name === 'CLS' ? 1000 : 1) });
}
